import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PayrollService } from '@/services/payroll.service';
import { HRService } from '@/services/hr.service';
import {
  Deduction,
  DeductionType,
  DeductionCategory,
  DeductionCalculationMethod,
  DeductionFrequency,
  DeductionStatus,
  DeductionTier,
  GarnishmentType,
} from '@/models/payroll.model';
import { useAuth } from '@/contexts/AuthContext';

export const DeductionForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const isEditing = !!id;
  const employeeId = searchParams.get('employeeId') || '';

  const [activeTab, setActiveTab] = useState<'basic' | 'calculation' | 'limits' | 'garnishment' | 'compliance'>('basic');
  const [formData, setFormData] = useState<Partial<Deduction>>({
    employeeId,
    type: DeductionType.postTax,
    category: DeductionCategory.other,
    calculationMethod: DeductionCalculationMethod.fixedAmount,
    frequency: DeductionFrequency.perPaycheck,
    status: DeductionStatus.active,
    priority: 999,
    isMandatory: false,
    requiresApproval: false,
    requiresEmployeeConsent: false,
    totalDeductedYTD: 0,
    totalDeductedLifetime: 0,
    deductionCount: 0,
    startDate: new Date(),
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tiers, setTiers] = useState<DeductionTier[]>([]);
  const [isGarnishment, setIsGarnishment] = useState(false);

  const { data: deduction, isLoading } = useQuery({
    queryKey: ['deduction', id],
    queryFn: () => (id ? PayrollService.getDeductionById(id) : null),
    enabled: isEditing && !!id,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => HRService.getAllEmployees(),
  });

  const selectedEmployee = employees.find((e) => e.id === formData.employeeId);

  React.useEffect(() => {
    if (deduction) {
      setFormData(deduction);
      setIsGarnishment(deduction.type === DeductionType.garnishment);
      if (deduction.tiers) {
        setTiers(deduction.tiers);
      }
    }
  }, [deduction]);

  React.useEffect(() => {
    if (formData.type === DeductionType.garnishment) {
      setIsGarnishment(true);
      if (!formData.garnishmentDetails) {
        setFormData({
          ...formData,
          garnishmentDetails: {
            type: GarnishmentType.other,
            priority: 1,
            isActive: true,
          },
        });
      }
    } else {
      setIsGarnishment(false);
    }
  }, [formData.type]);

  const mutation = useMutation({
    mutationFn: async (data: Partial<Deduction>) => {
      if (isEditing && id) {
        await PayrollService.updateDeduction(id, data, profile?.id || '');
      } else {
        await PayrollService.createDeduction(data, profile?.id || '');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductions'] });
      navigate('/payroll?tab=deductions');
    },
    onError: (error: any) => {
      setErrorMessage(error?.message || 'Failed to save deduction');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.employeeId || !formData.name) {
      setErrorMessage('Employee and deduction name are required');
      return;
    }

    if (selectedEmployee) {
      formData.employeeName = selectedEmployee.fullName;
    }

    if (formData.calculationMethod === DeductionCalculationMethod.tiered && tiers.length > 0) {
      formData.tiers = tiers;
    }

    mutation.mutate(formData);
  };

  const addTier = () => {
    setTiers([...tiers, { from: 0, amount: 0 }]);
  };

  const removeTier = (index: number) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const updateTier = (index: number, field: keyof DeductionTier, value: any) => {
    const newTiers = [...tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setTiers(newTiers);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEditing ? 'Edit Deduction' : 'New Deduction'}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg">
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 m-6">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {(['basic', 'calculation', 'limits', 'garnishment', 'compliance'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Employee *</label>
                  <select
                    required
                    value={formData.employeeId}
                    onChange={(e) =>
                      setFormData({ ...formData, employeeId: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Deduction Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 401(k) Contribution"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Type *</label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value as DeductionType })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.values(DeductionType).map((type) => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Category *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value as DeductionCategory })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.values(DeductionCategory).map((category) => (
                      <option key={category} value={category}>
                        {category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value as DeductionStatus })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.values(DeductionStatus).map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Frequency *</label>
                  <select
                    required
                    value={formData.frequency}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        frequency: e.target.value as DeductionFrequency,
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.values(DeductionFrequency).map((freq) => (
                      <option key={freq} value={freq}>
                        {freq.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={
                      formData.startDate
                        ? new Date(formData.startDate).toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFormData({ ...formData, startDate: new Date(e.target.value) })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={
                      formData.endDate
                        ? new Date(formData.endDate).toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        endDate: e.target.value ? new Date(e.target.value) : undefined,
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Priority</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.priority ?? 999}
                    onChange={(e) =>
                      setFormData({ ...formData, priority: parseInt(e.target.value) || 999 })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Lower number = higher priority (applied first)
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {/* Calculation Tab */}
          {activeTab === 'calculation' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Calculation Method *
                </label>
                <select
                  required
                  value={formData.calculationMethod}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      calculationMethod: e.target.value as DeductionCalculationMethod,
                    })
                  }
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {Object.values(DeductionCalculationMethod).map((method) => (
                    <option key={method} value={method}>
                      {method.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {formData.calculationMethod === DeductionCalculationMethod.fixedAmount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={formData.amount || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {formData.calculationMethod === DeductionCalculationMethod.percentage && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Percentage *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      required
                      value={formData.percentage || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          percentage: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Percentage Base
                    </label>
                    <select
                      value={formData.percentageBase || 'gross'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          percentageBase: e.target.value as 'gross' | 'net' | 'taxable',
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="gross">Gross Pay</option>
                      <option value="taxable">Taxable Income</option>
                      <option value="net">Net Pay</option>
                    </select>
                  </div>
                </>
              )}

              {formData.calculationMethod === DeductionCalculationMethod.tiered && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700">Tiers</label>
                    <button
                      type="button"
                      onClick={addTier}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                    >
                      + Add Tier
                    </button>
                  </div>
                  {tiers.map((tier, index) => (
                    <div key={index} className="grid grid-cols-4 gap-4 mb-2">
                      <input
                        type="number"
                        placeholder="From"
                        value={tier.from || ''}
                        onChange={(e) =>
                          updateTier(index, 'from', parseFloat(e.target.value) || 0)
                        }
                        className="border border-gray-300 rounded-md py-2 px-3"
                      />
                      <input
                        type="number"
                        placeholder="To (optional)"
                        value={tier.to || ''}
                        onChange={(e) =>
                          updateTier(index, 'to', e.target.value ? parseFloat(e.target.value) : undefined)
                        }
                        className="border border-gray-300 rounded-md py-2 px-3"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Amount"
                        value={tier.amount || ''}
                        onChange={(e) =>
                          updateTier(index, 'amount', parseFloat(e.target.value) || 0)
                        }
                        className="border border-gray-300 rounded-md py-2 px-3"
                      />
                      <div className="flex gap-2">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="%"
                          value={tier.percentage || ''}
                          onChange={(e) =>
                            updateTier(index, 'percentage', e.target.value ? parseFloat(e.target.value) : undefined)
                          }
                          className="border border-gray-300 rounded-md py-2 px-3 flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => removeTier(index)}
                          className="bg-red-600 text-white px-3 py-2 rounded text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Limits Tab */}
          {activeTab === 'limits' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Minimum Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.limits?.minimum || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        limits: {
                          ...formData.limits,
                          minimum: e.target.value ? parseFloat(e.target.value) : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Maximum Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.limits?.maximum || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        limits: {
                          ...formData.limits,
                          maximum: e.target.value ? parseFloat(e.target.value) : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Annual Maximum
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.limits?.annualMaximum || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        limits: {
                          ...formData.limits,
                          annualMaximum: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Lifetime Maximum
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.limits?.lifetimeMaximum || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        limits: {
                          ...formData.limits,
                          lifetimeMaximum: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Per Paycheck Maximum
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.limits?.perPaycheckMaximum || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        limits: {
                          ...formData.limits,
                          perPaycheckMaximum: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Garnishment Tab */}
          {activeTab === 'garnishment' && isGarnishment && formData.garnishmentDetails && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Garnishment Type *
                  </label>
                  <select
                    required
                    value={formData.garnishmentDetails.type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          type: e.target.value as GarnishmentType,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.values(GarnishmentType).map((type) => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Priority *</label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={formData.garnishmentDetails.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          priority: parseInt(e.target.value) || 1,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Case Number</label>
                  <input
                    type="text"
                    value={formData.garnishmentDetails.caseNumber || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          caseNumber: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Court Order Number
                  </label>
                  <input
                    type="text"
                    value={formData.garnishmentDetails.courtOrderNumber || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          courtOrderNumber: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Court Order Date
                  </label>
                  <input
                    type="date"
                    value={
                      formData.garnishmentDetails.courtOrderDate
                        ? new Date(formData.garnishmentDetails.courtOrderDate)
                            .toISOString()
                            .split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          courtOrderDate: e.target.value ? new Date(e.target.value) : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Court Order Expiry Date
                  </label>
                  <input
                    type="date"
                    value={
                      formData.garnishmentDetails.courtOrderExpiryDate
                        ? new Date(formData.garnishmentDetails.courtOrderExpiryDate)
                            .toISOString()
                            .split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          courtOrderExpiryDate: e.target.value
                            ? new Date(e.target.value)
                            : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Exempt Amount (Protected Income)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.garnishmentDetails.exemptAmount || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          exemptAmount: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Maximum Percentage (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.garnishmentDetails.maximumPercentage || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          maximumPercentage: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Processing Fee
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.garnishmentDetails.processingFee || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          processingFee: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        },
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Is Active</label>
                  <input
                    type="checkbox"
                    checked={formData.garnishmentDetails.isActive}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        garnishmentDetails: {
                          ...formData.garnishmentDetails!,
                          isActive: e.target.checked,
                        },
                      })
                    }
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Compliance Tab */}
          {activeTab === 'compliance' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Requires Approval
                  </label>
                  <input
                    type="checkbox"
                    checked={formData.requiresApproval || false}
                    onChange={(e) =>
                      setFormData({ ...formData, requiresApproval: e.target.checked })
                    }
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Is Mandatory</label>
                  <input
                    type="checkbox"
                    checked={formData.isMandatory || false}
                    onChange={(e) => setFormData({ ...formData, isMandatory: e.target.checked })}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Mandatory deductions cannot be cancelled by employee
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Requires Employee Consent
                  </label>
                  <input
                    type="checkbox"
                    checked={formData.requiresEmployeeConsent || false}
                    onChange={(e) =>
                      setFormData({ ...formData, requiresEmployeeConsent: e.target.checked })
                    }
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Employee Consent Date
                  </label>
                  <input
                    type="date"
                    value={
                      formData.employeeConsentDate
                        ? new Date(formData.employeeConsentDate).toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        employeeConsentDate: e.target.value ? new Date(e.target.value) : undefined,
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Legal Document URL
                  </label>
                  <input
                    type="url"
                    value={formData.legalDocumentUrl || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, legalDocumentUrl: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Compliance Notes
                  </label>
                  <textarea
                    value={formData.complianceNotes || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, complianceNotes: e.target.value })
                    }
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/payroll?tab=deductions')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

